// src/pages/admin/settings/index.tsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { fetchSystemSettings, updateSystemSettings } from '@/lib/api';
// import { getSetting, upsertSetting, fetchDeliveryFeeConfiguration, updateDeliveryFeeConfiguration } from '@/integrations/supabase/client';
import { getSetting, upsertSetting } from '@/integrations/supabase/client'; // Keep existing needed imports
import { fetchDeliveryFeeConfiguration, updateDeliveryFeeConfiguration } from '@/lib/deliverySettings'; // Import from new file

import LegalContentEditor from '@/components/admin/settings/LegalContentEditor';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Save } from 'lucide-react';

// Zod schema for the main form (general settings)
const generalFormSchema = z.object({
  store_name: z.string().min(2, { message: 'O nome da loja é obrigatório' }),
  order_whatsapp: z.string().optional(),
  contact_phone: z.string().optional(),
  store_address: z.string().optional(),
  business_hours: z.string().optional(),
  website_url: z.string().url({ message: 'Insira uma URL válida' }).optional().or(z.literal(''))
,
  instagram: z.string().optional(),
  facebook: z.string().optional(),
  tiktok: z.string().optional(),
  pix_key: z.string().optional(),
  bank_name: z.string().optional(),
  bank_agency: z.string().optional(),
  bank_account: z.string().optional(),
  bank_holder_name: z.string().optional(),
  bank_document: z.string().optional(),
});
type GeneralFormValues = z.infer<typeof generalFormSchema>;

// Zod schema for Delivery Fee Settings
const deliveryFeeSchema = z.object({
  fee_type: z.enum(['fixed', 'per_km'], { required_error: 'Selecione o tipo de taxa.' }),
  fixed_rate: z.coerce.number().nonnegative("A taxa fixa deve ser um valor positivo ou zero.").optional().nullable(),
  per_km_rate: z.coerce.number().nonnegative("A taxa por KM deve ser um valor positivo ou zero.").optional().nullable(),
  base_fee_for_per_km: z.coerce.number().nonnegative("A taxa base deve ser um valor positivo ou zero.").optional().nullable(),
}).superRefine((data, ctx) => {
  if (data.fee_type === 'fixed' && (data.fixed_rate === null || data.fixed_rate === undefined || data.fixed_rate < 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Taxa fixa é obrigatória e deve ser maior ou igual a zero.',
      path: ['fixed_rate'],
    });
  }
  if (data.fee_type === 'per_km' && (data.per_km_rate === null || data.per_km_rate === undefined || data.per_km_rate < 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Taxa por KM é obrigatória e deve ser maior ou igual a zero.',
      path: ['per_km_rate'],
    });
  }
});
type DeliveryFeeFormValues = z.infer<typeof deliveryFeeSchema>;


interface PaymentInfo {
  pix_key?: string;
  bank_name?: string;
  bank_agency?: string;
  bank_account?: string;
  bank_holder_name?: string;
  bank_document?: string;
}

interface SocialMediaLinks {
  instagram?: string;
  facebook?: string;
  tiktok?: string;
}

const defaultDeliveryFeeValues: DeliveryFeeFormValues = {
  fee_type: 'fixed',
  fixed_rate: 0,
  per_km_rate: 0,
  base_fee_for_per_km: 0,
};


const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('store');
  const queryClient = useQueryClient();

  const [termsContent, setTermsContent] = useState<string>('');
  const [privacyContent, setPrivacyContent] = useState<string>('');
  const [returnContent, setReturnContent] = useState<string>('');
  const [taxContent, setTaxContent] = useState<string>('');
  const [isLegalLoading, setIsLegalLoading] = useState(true);
  const [isLegalSaving, setIsLegalSaving] = useState(false);

  const { data: settings, isLoading: isSettingsLoading, error: fetchError } = useQuery({
    queryKey: ['system-settings'],
    queryFn: fetchSystemSettings,
  });

  const { mutate: mutateGeneralSettings, isPending: isSettingsSaving, error: mutationError } = useMutation({
    mutationFn: updateSystemSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success('Configurações gerais atualizadas com sucesso!');
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar configurações gerais: ${error.message}`);
    },
  });

  const generalForm = useForm<GeneralFormValues>({
    resolver: zodResolver(generalFormSchema),
    defaultValues: { /* Initialized below in useEffect */ },
  });

  useEffect(() => {
    if (settings) {
      const socialMedia = settings.social_media_links ?
        (typeof settings.social_media_links === 'object' ? settings.social_media_links as SocialMediaLinks : {}) : {};
      const paymentInfo = settings.payment_info ?
        (typeof settings.payment_info === 'object' ? settings.payment_info as PaymentInfo : {}) : {};
      generalForm.reset({
        store_name: settings.store_name || '',
        order_whatsapp: settings.order_whatsapp || '',
        contact_phone: settings.contact_phone || '',
        store_address: settings.store_address || '',
        business_hours: settings.business_hours || '',
        website_url: settings.website_url || '',
        instagram: socialMedia.instagram || '',
        facebook: socialMedia.facebook || '',
        tiktok: socialMedia.tiktok || '',
        pix_key: paymentInfo.pix_key || '',
        bank_name: paymentInfo.bank_name || '',
        bank_agency: paymentInfo.bank_agency || '',
        bank_account: paymentInfo.bank_account || '',
        bank_holder_name: paymentInfo.bank_holder_name || '',
        bank_document: paymentInfo.bank_document || '',
      });
    }
  }, [settings, generalForm]);

  const onGeneralSubmit = (values: GeneralFormValues) => {
    const socialMediaLinks = {
      instagram: values.instagram || '',
      facebook: values.facebook || '',
      tiktok: values.tiktok || ''
    };
    const paymentInfo = {
      pix_key: values.pix_key || '',
      bank_name: values.bank_name || '',
      bank_agency: values.bank_agency || '',
      bank_account: values.bank_account || '',
      bank_holder_name: values.bank_holder_name || '',
      bank_document: values.bank_document || '',
    };
    const {
      instagram, facebook, tiktok,
      pix_key, bank_name, bank_agency, bank_account, bank_holder_name, bank_document,
      ...settingsData
    } = values;
    const dataToSubmit = {
      ...settingsData,
      social_media_links: socialMediaLinks,
      payment_info: paymentInfo
    };
    mutateGeneralSettings(dataToSubmit);
  };

  useEffect(() => {
    const loadLegalContent = async () => {
      setIsLegalLoading(true);
      try {
        const [terms, privacy, returns, tax] = await Promise.all([
          getSetting('terms_content'),
          getSetting('privacy_content'),
          getSetting('return_content'),
          getSetting('tax_content'),
        ]);
        setTermsContent(terms ?? '');
        setPrivacyContent(privacy ?? '');
        setReturnContent(returns ?? '');
        setTaxContent(tax ?? '');
      } catch (error) {
        console.error("Error loading legal content:", error);
        toast.error("Erro ao carregar conteúdo legal.");
      } finally {
        setIsLegalLoading(false);
      }
    };
    loadLegalContent();
  }, []);

  const handleSaveLegal = async (key: string, content: string) => {
    setIsLegalSaving(true);
    try {
      const success = await upsertSetting(key, content);
      if (success) {
        toast.success(`Conteúdo de ${key.replace('_content','').replace('_',' ')} salvo com sucesso!`);
        if (key === 'terms_content') setTermsContent(content);
        else if (key === 'privacy_content') setPrivacyContent(content);
        else if (key === 'return_content') setReturnContent(content);
        else if (key === 'tax_content') setTaxContent(content);
        queryClient.invalidateQueries({ queryKey: ['settings', key] });
      } else {
        toast.error(`Erro ao salvar ${key.replace('_content','').replace('_',' ')}.`);
      }
    } catch (error) {
      console.error(`Error saving legal content for ${key}:`, error);
      toast.error(`Erro crítico ao salvar ${key.replace('_content','').replace('_',' ')}.`);
    } finally {
      setIsLegalSaving(false);
    }
  };

  const deliveryFeeForm = useForm<DeliveryFeeFormValues>({
    resolver: zodResolver(deliveryFeeSchema),
    defaultValues: defaultDeliveryFeeValues,
  });

  const { data: deliveryFeeConfig, isLoading: isDeliveryFeeLoading, error: deliveryFeeFetchError } = useQuery({
    queryKey: ['delivery-fee-config'],
    queryFn: fetchDeliveryFeeConfiguration,
    initialData: defaultDeliveryFeeValues,
    refetchOnWindowFocus: false, // Consider disabling refetch on window focus if not needed
  });
  
  const { mutate: mutateDeliveryFee, isPending: isDeliveryFeeSaving, error: deliveryFeeMutationError } = useMutation({
    mutationFn: updateDeliveryFeeConfiguration,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['delivery-fee-config'] });
      toast.success('Configurações de taxa de entrega atualizadas com sucesso!');
      if (data) { 
        deliveryFeeForm.reset({
            fee_type: data.fee_type || 'fixed',
            fixed_rate: data.fixed_rate !== null && data.fixed_rate !== undefined ? Number(data.fixed_rate) : 0,
            per_km_rate: data.per_km_rate !== null && data.per_km_rate !== undefined ? Number(data.per_km_rate) : 0,
            base_fee_for_per_km: data.base_fee_for_per_km !== null && data.base_fee_for_per_km !== undefined ? Number(data.base_fee_for_per_km) : 0,
        });
      }
    },
    onError: (error: any) => {
      console.error("Error updating delivery fees:", error);
      toast.error(`Erro ao atualizar taxas de entrega: ${error.message}`);
    },
  });

  useEffect(() => {
    if (deliveryFeeConfig) {
      deliveryFeeForm.reset({
        fee_type: deliveryFeeConfig.fee_type || 'fixed',
        fixed_rate: deliveryFeeConfig.fixed_rate !== null && deliveryFeeConfig.fixed_rate !== undefined ? Number(deliveryFeeConfig.fixed_rate) : 0,
        per_km_rate: deliveryFeeConfig.per_km_rate !== null && deliveryFeeConfig.per_km_rate !== undefined ? Number(deliveryFeeConfig.per_km_rate) : 0,
        base_fee_for_per_km: deliveryFeeConfig.base_fee_for_per_km !== null && deliveryFeeConfig.base_fee_for_per_km !== undefined ? Number(deliveryFeeConfig.base_fee_for_per_km) : 0,
      });
    }
  }, [deliveryFeeConfig, deliveryFeeForm]);
  
  const onDeliveryFeeSubmit = (values: DeliveryFeeFormValues) => {
    const dataToSubmit = {
      id: 1, // Ensure the ID is always 1 for the single configuration row
      fee_type: values.fee_type,
      fixed_rate: values.fee_type === 'fixed' ? (values.fixed_rate ?? 0) : null,
      per_km_rate: values.fee_type === 'per_km' ? (values.per_km_rate ?? 0) : null,
      base_fee_for_per_km: values.fee_type === 'per_km' ? (values.base_fee_for_per_km ?? 0) : null,
    };
    mutateDeliveryFee(dataToSubmit); 
  };

  const watchedFeeType = deliveryFeeForm.watch('fee_type');

  if (isSettingsLoading || isDeliveryFeeLoading) { 
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-navy-600" />
        <span className="ml-2 text-lg">Carregando configurações...</span>
      </div>
    );
  }

  if (fetchError) console.error("LOG: Settings - Fetch error (general):", fetchError);
  if (mutationError) console.error("LOG: Settings - Mutation error (general):", mutationError);
  if (deliveryFeeFetchError) console.error("LOG: Settings - Fetch error (delivery):", deliveryFeeFetchError);
  if (deliveryFeeMutationError) console.error("LOG: Settings - Mutation error (delivery):", deliveryFeeMutationError);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Configurações do Sistema</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Gerencie as configurações gerais, legais e de entrega da sua loja.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4 grid w-full grid-cols-4">
          <TabsTrigger value="store">Informações da Loja</TabsTrigger>
          <TabsTrigger value="payment">Pagamento</TabsTrigger>
          <TabsTrigger value="delivery">Taxas de Entrega</TabsTrigger>
          <TabsTrigger value="legal">Informações Legais</TabsTrigger>
        </TabsList>

        <TabsContent value="store">
          <Form {...generalForm}>
            <form onSubmit={generalForm.handleSubmit(onGeneralSubmit)} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Básicas</CardTitle>
                  <CardDescription>Configure as informações principais da loja</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField control={generalForm.control} name="store_name" render={({ field }) => ( <FormItem> <FormLabel>Nome da Loja</FormLabel> <FormControl><Input placeholder="Nome da sua loja" {...field} /></FormControl> <FormDescription>Este nome será exibido em todo o aplicativo</FormDescription> <FormMessage /> </FormItem> )}/>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField control={generalForm.control} name="order_whatsapp" render={({ field }) => ( <FormItem> <FormLabel>WhatsApp para Pedidos</FormLabel> <FormControl><Input placeholder="+5511999999999" {...field} /></FormControl> <FormDescription>Número para onde serão enviados os pedidos (com código do país)</FormDescription> <FormMessage /> </FormItem> )}/>
                    <FormField control={generalForm.control} name="contact_phone" render={({ field }) => ( <FormItem> <FormLabel>Telefone para Contato</FormLabel> <FormControl><Input placeholder="(11) 99999-9999" {...field} /></FormControl> <FormDescription>Número para contato geral da loja</FormDescription> <FormMessage /> </FormItem> )}/>
                  </div>
                  <FormField control={generalForm.control} name="store_address" render={({ field }) => ( <FormItem> <FormLabel>Endereço da Loja</FormLabel> <FormControl><Textarea placeholder="Endereço completo da loja física" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                  <FormField control={generalForm.control} name="business_hours" render={({ field }) => ( <FormItem> <FormLabel>Horário de Funcionamento</FormLabel> <FormControl><Textarea placeholder="Ex: Segunda à Sexta: 9h às 18h, Sábado: 9h às 13h" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                  <FormField control={generalForm.control} name="website_url" render={({ field }) => ( <FormItem> <FormLabel>Website</FormLabel> <FormControl><Input placeholder="https://www.seusite.com.br" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                </CardContent>
              </Card>
              <Card className="mt-6">
                 <CardHeader><CardTitle>Redes Sociais</CardTitle><CardDescription>Configure os links para suas redes sociais</CardDescription></CardHeader>
                <CardContent className="space-y-4">
                 <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                   <FormField control={generalForm.control} name="instagram" render={({ field }) => ( <FormItem> <FormLabel>Instagram</FormLabel> <FormControl><Input placeholder="@seuinstagram ou URL completa" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                   <FormField control={generalForm.control} name="facebook" render={({ field }) => ( <FormItem> <FormLabel>Facebook</FormLabel> <FormControl><Input placeholder="URL da página do Facebook" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                  </div>
                  <FormField control={generalForm.control} name="tiktok" render={({ field }) => ( <FormItem> <FormLabel>TikTok</FormLabel> <FormControl><Input placeholder="@seutiktok ou URL completa" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                </CardContent>
               </Card>
                {(activeTab === 'store') && (
                <div className="flex justify-end mt-6">
                    <Button type="submit" disabled={isSettingsSaving}> {isSettingsSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} <Save className="mr-2 h-4 w-4" /> Salvar Alterações (Loja) </Button>
                </div>
                )}
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="payment">
             <Form {...generalForm}>
                <form onSubmit={generalForm.handleSubmit(onGeneralSubmit)} className="space-y-6">
                    <Card>
                        <CardHeader><CardTitle>Configurações de Pagamento</CardTitle><CardDescription>Configure as informações de pagamento da loja</CardDescription></CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <h3 className="text-lg font-medium mb-4">PIX</h3>
                                <FormField control={generalForm.control} name="pix_key" render={({ field }) => ( <FormItem> <FormLabel>Chave PIX</FormLabel> <FormControl><Input placeholder="CPF, CNPJ, email, telefone ou chave aleatória" {...field} /></FormControl> <FormDescription>Esta chave será exibida para os clientes no checkout</FormDescription> <FormMessage /> </FormItem> )}/>
                            </div>
                            <Separator />
                            <div>
                                <h3 className="text-lg font-medium mb-4">Dados Bancários</h3>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <FormField control={generalForm.control} name="bank_name" render={({ field }) => ( <FormItem> <FormLabel>Nome do Banco</FormLabel> <FormControl><Input placeholder="Ex: Banco do Brasil" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                                    <FormField control={generalForm.control} name="bank_agency" render={({ field }) => ( <FormItem> <FormLabel>Agência</FormLabel> <FormControl><Input placeholder="Ex: 1234-5" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                                </div>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
                                    <FormField control={generalForm.control} name="bank_account" render={({ field }) => ( <FormItem> <FormLabel>Conta</FormLabel> <FormControl><Input placeholder="Ex: 12345-6" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                                    <FormField control={generalForm.control} name="bank_holder_name" render={({ field }) => ( <FormItem> <FormLabel>Nome do Titular</FormLabel> <FormControl><Input placeholder="Nome completo do titular da conta" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                                </div>
                                <div className="mt-4">
                                    <FormField control={generalForm.control} name="bank_document" render={({ field }) => ( <FormItem> <FormLabel>CPF/CNPJ do Titular</FormLabel> <FormControl><Input placeholder="Documento do titular da conta" {...field} /></FormControl> <FormMessage /> </FormItem> )}/>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    {(activeTab === 'payment') && (
                    <div className="flex justify-end mt-6">
                        <Button type="submit" disabled={isSettingsSaving}> {isSettingsSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} <Save className="mr-2 h-4 w-4" /> Salvar Alterações (Pagamento) </Button>
                    </div>
                    )}
                </form>
            </Form>
        </TabsContent>

        <TabsContent value="delivery">
          <Form {...deliveryFeeForm}>
            <form onSubmit={deliveryFeeForm.handleSubmit(onDeliveryFeeSubmit)} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Taxas de Entrega</CardTitle>
                  <CardDescription>Configure como as taxas de entrega serão calculadas.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={deliveryFeeForm.control}
                    name="fee_type"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>Tipo de Taxa de Entrega</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value} // Ensure value is controlled
                            className="flex flex-col space-y-1"
                          >
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl><RadioGroupItem value="fixed" /></FormControl>
                              <FormLabel className="font-normal">Taxa Fixa</FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl><RadioGroupItem value="per_km" /></FormControl>
                              <FormLabel className="font-normal">Taxa por KM</FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {watchedFeeType === 'fixed' && (
                    <FormField
                      control={deliveryFeeForm.control}
                      name="fixed_rate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Valor da Taxa Fixa (R$)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="Ex: 10.00" {...field} 
                            value={field.value ?? ''} // Handle null/undefined for input display
                            onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))} />
                          </FormControl>
                          <FormDescription>Informe o valor fixo para a entrega.</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {watchedFeeType === 'per_km' && (
                    <>
                      <FormField
                        control={deliveryFeeForm.control}
                        name="per_km_rate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Valor por KM (R$)</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="Ex: 2.50" {...field} 
                              value={field.value ?? ''} // Handle null/undefined for input display
                              onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}/>
                            </FormControl>
                            <FormDescription>Informe o valor cobrado por quilômetro.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={deliveryFeeForm.control}
                        name="base_fee_for_per_km"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Taxa Base Adicional por KM (R$) (Opcional)</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="Ex: 5.00" {...field} 
                              value={field.value ?? ''} // Handle null/undefined for input display
                              onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}/>
                            </FormControl>
                            <FormDescription>Uma taxa base que é somada ao cálculo por KM (ex: custo de saída).</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </CardContent>
              </Card>
              <div className="flex justify-end mt-6">
                <Button type="submit" disabled={isDeliveryFeeSaving || isDeliveryFeeLoading}>
                  {(isDeliveryFeeSaving || isDeliveryFeeLoading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" />
                  Salvar Configurações de Entrega
                </Button>
              </div>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="legal">
          <Card>
            <CardHeader>
              <CardTitle>Informações Legais</CardTitle>
              <CardDescription>
                Edite os termos e políticas da loja que serão exibidos aos clientes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLegalLoading ? (
                <div className="flex items-center justify-center p-6">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-500 mr-2" />
                  <span>Carregando conteúdo legal...</span>
                </div>
              ) : (
                <div>
                  <LegalContentEditor
                    title="Termos e Condições de Uso"
                    initialContent={termsContent}
                    onSave={(content) => handleSaveLegal('terms_content', content)}
                    isSaving={isLegalSaving}
                  />
                  <Separator className="my-6" />
                  <LegalContentEditor
                    title="Política de Privacidade"
                    initialContent={privacyContent}
                    onSave={(content) => handleSaveLegal('privacy_content', content)}
                    isSaving={isLegalSaving}
                  />
                   <Separator className="my-6" />
                  <LegalContentEditor
                    title="Política de Devolução"
                    initialContent={returnContent}
                    onSave={(content) => handleSaveLegal('return_content', content)}
                    isSaving={isLegalSaving}
                  />
                   <Separator className="my-6" />
                  <LegalContentEditor
                    title="CNPJ e Informações Fiscais"
                    initialContent={taxContent}
                    onSave={(content) => handleSaveLegal('tax_content', content)}
                    isSaving={isLegalSaving}
                  />
                  {isLegalSaving && (
                     <div className="fixed bottom-4 right-4 z-50 flex items-center justify-center p-4 bg-blue-100 border border-blue-300 rounded-md shadow-lg">
                       <Loader2 className="h-5 w-5 animate-spin text-blue-600 mr-2" />
                       <span>Salvando conteúdo legal...</span>
                     </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
